<template>
  <div>
    <!-- The component is wrapped in a link if the `link` prop is provided -->
    <router-link v-if="link" :to="link" class="carousel-main-link">
      <div class="mini-carousel-container" :style="{ background: `linear-gradient(to bottom, ${color}, #050404)` }">
        <div class="carousel-images">
          <img
            v-for="(photo, idx) in images"
            :key="idx"
            class="carousel-thumbnail"
            :alt="imageCaptions[idx] || 'carousel image'"
            v-lazy="photo + '?w=80&h=80&fit=crop&auto=format'"
          />
        </div>
        <p v-if="title" class="carousel-title">{{ title }}</p>
      </div>
    </router-link>

    <!-- The component is rendered as a standalone gallery if no `link` prop is provided -->
    <div v-else class="mini-carousel-container" :style="{ background: `linear-gradient(to bottom, ${color}, #050404)` }">
      <div class="carousel-images">
        <img
          v-for="(photo, idx) in images"
          :key="idx"
          class="carousel-thumbnail"
          :alt="imageCaptions[idx] || 'carousel image'"
          v-lazy="photo + '?w=80&h=80&fit=crop&auto=format'"
          @click="viewImage(photo, imageCaptions[idx])"
        />
      </div>
      <p v-if="title" class="carousel-title">{{ title }}</p>
    </div>

    <!-- Lightbox Overlay for viewing images -->
    <transition name="fade">
      <div v-if="isLightboxOpen" class="lightbox-overlay" @click="closeLightbox">
        <div class="lightbox-content">
          <img :src="selectedImage + '?w=1200&auto=format'" :alt="selectedCaption" class="lightbox-image">
          <p v-if="selectedCaption" class="lightbox-caption">{{ selectedCaption }}</p>
          <button class="lightbox-close" @click="closeLightbox">&times;</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { MEDIA_BASE_URL } from '@/common/request';

export default {
  name: 'MiniCarrousel',
  props: {
    photos: {
      type: Array,
      default: () => [],
    },
    captions: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: null,
    },
    numPhotos: {
      type: Number,
      default: 6,
    },
    color: {
      type: String,
      default: '#252528',
    },
    folder: {
      type: String,
      default: null,
    },
    // ✅ NEW PROPS
    link: {
      type: [String, Object], // Can be a string path or a router-link object
      default: null,
    },
    enableLightbox: {
      type: Boolean,
      default: true, // Lightbox is enabled by default
    },
  },
  data() {
    return {
      images: [],
      imageCaptions: [],
      isLightboxOpen: false,
      selectedImage: null,
      selectedCaption: '',
    };
  },
  mounted() {
    if (this.folder) {
      this.fetchImagesFromFolder();
    } else {
      this.images = this.photos.slice(0, this.numPhotos);
      this.imageCaptions = this.captions;
    }
  },
  methods: {
    fetchImagesFromFolder() {
      // Note: Ensure your read_key is secure and not exposed if this is a private bucket.
      fetch(encodeURI(`${MEDIA_BASE_URL}?props=imgix_url,metadata&read_key=7pMG22sp4oEfuIRswb3c9kfsxlfxa9vxrGAOUBJttBwiP4FkGQ&query={"folder": "${this.folder}"}`))
        .then(response => response.json())
        .then(data => {
          if (data.media) {
            const sorted = [...data.media].sort((a, b) => (a?.metadata?.order || 100) > (y?.metadata?.order || 99) ? 1 : -1);
            this.images = sorted.map(item => item.imgix_url).slice(0, this.numPhotos);
            this.imageCaptions = sorted.map(item => item.metadata?.caption || '');
          }
        })
        .catch(error => {
          console.error("Error fetching carousel images:", error);
        });
    },
    viewImage(imageUrl, caption) {
      if (!this.enableLightbox || this.link) return; // Do nothing if lightbox is off or if it's a link
      this.selectedImage = imageUrl;
      this.selectedCaption = caption;
      this.isLightboxOpen = true;
    },
    closeLightbox() {
      this.isLightboxOpen = false;
    },
  },
};
</script>

<style scoped>
.carousel-main-link {
  text-decoration: none;
  color: inherit;
}

.mini-carousel-container {
  padding: 1rem;
  border-radius: 12px;
  text-align: center;
}

.carousel-images {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.carousel-thumbnail {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: transform 0.3s ease, border-color 0.3s ease;
}

.carousel-thumbnail:hover {
  transform: scale(1.05);
  border-color: #00c6ff;
}

.carousel-title {
  margin-top: 1rem;
  font-weight: bold;
  color: #f0f0f0;
}

/* Lightbox Styles */
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
}

.lightbox-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.lightbox-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
}

.lightbox-caption {
  color: #fff;
  text-align: center;
  margin-top: 1rem;
  font-style: italic;
}

.lightbox-close {
  position: absolute;
  top: -40px;
  right: -20px;
  background: none;
  border: none;
  color: #fff;
  font-size: 3rem;
  cursor: pointer;
}

/* Fade Transition for Lightbox */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
